/*                                                */
import { untrack } from "svelte";
import { componentsScope } from "../../../../common/utils/logger.js";

export const dialogScope = /*           */ componentsScope.scope("focused-dialog-v1");

export type UrlParameterType = "close" | "prev";
export type ParamDefinition = [modernName: string, legacyName: string];

export const prefix = "oc-focused-dialog-v1";
export const closeParam = [
  `${prefix}.close-url`,
  "focussedDialogCloseUrl",
] as const satisfies ParamDefinition;
export const prevParam = [
  `${prefix}.prev-url`,
  "focussedDialogPrevUrl",
] as const satisfies ParamDefinition;

export const paramsMap = {
  close: closeParam,
  prev: prevParam,
} as const;

export const getFirstAvailableParam = (
  definition: ParamDefinition,
  defaultValue: string | undefined,
  params = new URLSearchParams(location.search),
): string | undefined => {
  for (const key of definition) {
    const value = params.get(key) ?? undefined;
    if (typeof value === "string") return value;
  }
  return defaultValue;
};

export function isSafeUrl(
  url: string,
  { host: currentHost, protocol: currentProtocol, href } = location,
): boolean {
  try {
    const { host, protocol, pathname } = new URL(url, href);

    /*                                                                             */
    if (/\/{2,}/g.test(pathname)) {
      return false;
    }

    const isSafe = host === currentHost && protocol === currentProtocol;

    if (!isSafe) {
      dialogScope.warn("is not safe", url);
    }
    return isSafe;
  } catch (e) {
    dialogScope.warn(e);
    return false;
  }
}

export type UrlGuardedObservable = {
  orig: string | undefined;
} & (
  | {
      value: string;
      isSafe: true;
    }
  | {
      value: undefined;
      isSafe: false;
    }
);

export const useUrlParameter = (
  type: UrlParameterType,
  getProp: () => string | undefined,
  setProp: (value: string | undefined) => void,
): UrlGuardedObservable => {
  const state = $state<UrlGuardedObservable>({
    orig: undefined,
    value: undefined,
    isSafe: false,
  });

  const paramDef = paramsMap[type];

  $effect(() => {
    const propVal = getProp();

    const paramValue = getFirstAvailableParam(paramDef, propVal);

    /*                                    */
    if (untrack(() => state.orig) === paramValue) return;
    state.orig = paramValue;

    /*                                                      */
    if (!paramValue) {
      state.value = undefined;
      state.isSafe = false;

      return setProp(undefined);
    }

    const isSafe = isSafeUrl(paramValue);
    state.isSafe = isSafe;
    /*                                                       */
    if (!isSafe) {
      state.value = undefined;
      return setProp(undefined);
    }

    state.value = paramValue;
    state.isSafe = true;
    setProp(paramValue);
  });

  return state;
};
